import { useEffect, useState } from "react";
import {
  Trash,
  CurrencyCircleDollar,
  Receipt,
  Warning,
  Info,
  ArrowsClockwise,
  CalendarPlus,
} from "@phosphor-icons/react";
import { getTransactionByAppointment } from "../../firebase";
import LoaderLoop from "../shared/loader/LoaderLoop";
import "./DeleteAppointmentDialog.css";

const TX_PAID_FAMILY = new Set(["paid", "invoice_manual", "invoice_auto"]);

function describeTxStatus(tx) {
  if (!tx) return "";
  switch (tx.status) {
    case "paid":
      return "שולם";
    case "invoice_manual":
      return "שולם + חשבונית";
    case "invoice_auto":
      return "שולם + חשבונית (אוטומטי)";
    case "pending":
    case "pending_collection":
      return "ממתין לתשלום";
    case "expected":
      return "צפי הכנסה";
    case "refunded":
      return "זוכה";
    case "cancelled":
      return "בוטל";
    default:
      return tx.status || "";
  }
}

function formatTxAmount(value) {
  const numeric = parseFloat(value);
  if (!Number.isFinite(numeric)) return "";
  return `₪${numeric.toLocaleString("he-IL")}`;
}

function isAppointmentEnded(apt) {
  if (!apt) return false;
  if (apt.status === "completed") return true;
  if (!apt.endTime) return false;
  const endMs = new Date(apt.endTime).getTime();
  if (Number.isNaN(endMs)) return false;
  return endMs < Date.now();
}

/**
 * Shared delete-appointment dialog used by both `AppointmentModal` (footer
 * delete button) and `CalendarPage` (right-click context menu).
 *
 * Behaviour:
 *   - Pre-loads the linked `financial_transactions` row for the appointment.
 *   - Adapts the warning copy to whether the appointment is already past.
 *   - When a transaction exists, exposes a single checkbox letting the
 *     therapist opt-in to also hard-deleting that transaction (default OFF
 *     → the transaction is merely flipped to CANCELLED so the audit trail
 *     survives).
 *   - When the appointment has invoices in `RecepitsDocsRefArray`, shows a
 *     clear notice that the actual invoice in the external provider can't
 *     be cancelled from CliniKit and must be handled directly there.
 *
 * The component is fully self-contained for the preload + selection state.
 * The parent owns the actual mutation call and provides:
 *   @param {Object} props.appointment
 *   @param {string} props.clinicId
 *   @param {boolean} props.isOpen
 *   @param {boolean} [props.isWorking] - external "in flight" flag from the parent
 *   @param {(opts: { deleteLinkedTransaction: boolean }) => void} props.onConfirm
 *   @param {() => void} props.onClose
 *
 * Error Code: CK-3416 (preload)
 */
export default function DeleteAppointmentDialog({
  appointment,
  clinicId,
  isOpen,
  isWorking = false,
  onConfirm,
  onClose,
  // Optional: offered for series meetings as an alternative to deleting —
  // pushes this meeting and the rest of the series a week forward.
  onPostponeInstead,
}) {
  // `undefined` = still loading; `null` = loaded, no linked tx.
  const [linkedTransaction, setLinkedTransaction] = useState(undefined);
  const [preloadError, setPreloadError] = useState(null);
  const [deleteLinkedTransaction, setDeleteLinkedTransaction] = useState(false);

  // Reset state every time the dialog opens for a new appointment so a
  // previously toggled checkbox doesn't leak between unrelated deletes.
  useEffect(() => {
    if (!isOpen) return;
    setLinkedTransaction(undefined);
    setPreloadError(null);
    setDeleteLinkedTransaction(false);
  }, [isOpen, appointment?.id]);

  useEffect(() => {
    if (!isOpen || !appointment?.id || !clinicId) return;
    let cancelled = false;
    (async () => {
      try {
        const tx = await getTransactionByAppointment(clinicId, appointment.id);
        if (cancelled) return;
        setLinkedTransaction(tx || null);
      } catch (err) {
        console.error("[CK-3416] Error preloading transaction for delete dialog:", {
          errorCode: "CK-3416",
          errorMessage: err.message,
          appointmentId: appointment.id,
          timestamp: new Date().toISOString(),
        });
        if (cancelled) return;
        setLinkedTransaction((prev) => prev ?? null);
        setPreloadError(err.message || "שגיאה בטעינת התשלום המקושר");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, appointment?.id, clinicId]);

  if (!isOpen || !appointment) return null;

  const isPast = isAppointmentEnded(appointment);
  const isLoadingTx = linkedTransaction === undefined;
  const hasTx = !!linkedTransaction;
  const txIsPaidFamily = hasTx && TX_PAID_FAMILY.has(linkedTransaction.status);
  const hasInvoice =
    Array.isArray(appointment?.RecepitsDocsRefArray) &&
    appointment.RecepitsDocsRefArray.length > 0;

  // Postponing only makes sense for a series meeting that is still ahead of us.
  const canPostpone = (() => {
    if (typeof onPostponeInstead !== "function") return false;
    if (appointment.isRecurring !== true || !appointment.startTime) return false;
    const moved = new Date(appointment.startTime);
    moved.setDate(moved.getDate() + 7);
    return moved.getTime() > Date.now();
  })();

  const handleOverlayClick = (e) => {
    if (isWorking) return;
    if (e.target === e.currentTarget) onClose();
  };

  const handleConfirm = () => {
    onConfirm({
      deleteLinkedTransaction: hasTx && deleteLinkedTransaction,
    });
  };

  return (
    <div
      className="modal-overlay apt-delete-overlay"
      onClick={handleOverlayClick}
    >
      <div
        className="apt-delete-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="apt-delete-dialog-title"
      >
        <div className="dialog-header">
          <Trash size={24} weight="bold" className="dialog-icon" />
          <h3 id="apt-delete-dialog-title">מחיקת פגישה</h3>
        </div>

        <p className="dialog-message">
          הפגישה עם <strong>{appointment.patientName}</strong> תוסתר מהמערכת
          לחלוטין — היא לא תופיע ביומן, בפגישות ממתינות או בדו״חות. הנתונים
          יישמרו בבסיס הנתונים לצרכי ארכיון. פעולה זו לא ניתנת לביטול מהממשק.
        </p>

        {isPast && (
          <div className="apt-delete-context-note">
            <Info size={16} weight="duotone" />
            <span>
              פגישה זו כבר התקיימה. מחיקה לא תשנה תיעוד טיפול שכבר נכתב — הוא
              יישאר מקושר למטופל/ת.
            </span>
          </div>
        )}

        {appointment.isRecurring && (
          <div className="apt-delete-context-note apt-delete-series-note">
            <ArrowsClockwise size={16} weight="duotone" />
            <div>
              <span>
                הפגישה היא חלק מסדרה. תימחק רק הפגישה הזו — שאר הפגישות בסדרה
                יישארו במועדיהן, לא ייווצר מפגש חלופי והסדרה תסתיים באותו תאריך.
              </span>
              {canPostpone && (
                <button
                  type="button"
                  className="apt-delete-postpone-instead"
                  onClick={() => onPostponeInstead(appointment)}
                  disabled={isWorking}
                >
                  <CalendarPlus size={16} weight="duotone" />
                  דחה בשבוע במקום למחוק
                </button>
              )}
            </div>
          </div>
        )}

        <div className="apt-delete-payment-block">
          {isLoadingTx && (
            <div className="apt-delete-payment-loading">
              <LoaderLoop size="xs" />
              <span>בודק תיעוד תשלום למפגש זה…</span>
            </div>
          )}

          {!isLoadingTx && preloadError && (
            <div className="apt-delete-payment-error">
              <Warning size={16} weight="duotone" />
              <span>{preloadError}</span>
            </div>
          )}

          {!isLoadingTx && hasTx && (
            <>
              <div
                className={`apt-delete-payment-card ${
                  txIsPaidFamily ? "paid" : "pending"
                }`}
              >
                <CurrencyCircleDollar size={20} weight="duotone" />
                <div className="apt-delete-payment-text">
                  <strong>קיים תיעוד תשלום מקושר לפגישה</strong>
                  <span>
                    סטטוס: {describeTxStatus(linkedTransaction)}
                    {linkedTransaction.amount
                      ? ` · ${formatTxAmount(linkedTransaction.amount)}`
                      : ""}
                  </span>
                </div>
              </div>

              <label className="apt-delete-payment-checkbox">
                <input
                  type="checkbox"
                  checked={deleteLinkedTransaction}
                  onChange={(e) =>
                    setDeleteLinkedTransaction(e.target.checked)
                  }
                  disabled={isWorking}
                />
                <span>
                  מחק גם את תיעוד התשלום מהמערכת
                  <span className="apt-delete-hint">
                    {" "}
                    (אם לא תסומן — תיעוד שטרם שולם יסומן כ"בוטל"; תשלום שכבר התקבל יישאר כפי שהוא)
                  </span>
                </span>
              </label>

              {hasInvoice && (
                <div className="apt-delete-invoice-warning">
                  <Receipt size={18} weight="duotone" />
                  <div>
                    <strong>הופקה חשבונית עבור התשלום הזה.</strong>
                    <span>
                      {" "}
                      מחיקת התיעוד כאן לא מבטלת את החשבונית הרשמית במערכת ספק
                      החשבוניות. לזיכוי / ביטול חשבונית יש לפעול ישירות מול
                      הספק.
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="dialog-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={onClose}
            disabled={isWorking}
          >
            ביטול
          </button>
          <button
            type="button"
            className="recurring-delete-confirm-btn"
            onClick={handleConfirm}
            disabled={isWorking || isLoadingTx}
          >
            {isWorking ? "מוחק..." : "מחק"}
          </button>
        </div>
      </div>
    </div>
  );
}
